import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import type { TransactionSource } from '@/types/asset-import.types';

interface TransactionImportFiltersTabProps {
  readonly categories: string[];
  readonly suppliers: string[];
  readonly showExcluded: boolean;
  readonly selectedCategories: string[];
  readonly transactionSource?: TransactionSource;
  readonly supplier?: string;
  readonly onShowExcludedChange: (show: boolean) => void;
  readonly onCategoriesChange: (categories: string[]) => void;
  readonly onTransactionSourceChange: (source?: TransactionSource) => void;
  readonly onSupplierChange: (supplier?: string) => void;
}

export function TransactionImportFiltersTab({
  categories,
  suppliers,
  showExcluded,
  selectedCategories,
  transactionSource,
  supplier,
  onShowExcludedChange,
  onCategoriesChange,
  onTransactionSourceChange,
  onSupplierChange,
}: Readonly<TransactionImportFiltersTabProps>) {
  const handleCategoryToggle = (category: string, checked: boolean) => {
    if (checked) {
      onCategoriesChange([...selectedCategories, category]);
    } else {
      onCategoriesChange(selectedCategories.filter((c) => c !== category));
    }
  };

  const handleSelectAllCategories = () => {
    if (selectedCategories.length === categories.length) {
      onCategoriesChange([]);
    } else {
      onCategoriesChange(categories);
    }
  };

  return (
    <div className="space-y-6">
      {/* Show Excluded */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Display Options
        </Label>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="showExcluded"
            checked={showExcluded}
            onCheckedChange={onShowExcludedChange}
          />
          <label
            htmlFor="showExcluded"
            className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer"
          >
            Show already used transactions
          </label>
        </div>
      </div>

      {/* Transaction Source Filter */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Transaction Source
        </Label>
        <Select
          value={transactionSource ?? 'all'}
          onValueChange={(value: string) => onTransactionSourceChange(value === 'all' ? undefined : value as TransactionSource)}
        >
          <SelectTrigger>
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="purchase-ledger">Purchase Ledger</SelectItem>
            <SelectItem value="nominal-ledger">Nominal Ledger</SelectItem>
            <SelectItem value="costing">Costing</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Supplier Filter */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Supplier
        </Label>
        <Input
          type="text"
          placeholder="Filter by supplier..."
          value={supplier ?? ''}
          onChange={(e) => onSupplierChange(e.target.value ?? undefined)}
        />
      </div>

      {/* Category Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Categories
          </Label>
          <button
            onClick={handleSelectAllCategories}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {selectedCategories.length === categories.length ? 'Clear All' : 'Select All'}
          </button>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {categories.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No categories available</p>
          ) : (
            categories.map((category) => (
              <div key={category} className="flex items-center space-x-2">
                <Checkbox
                  id={`category-${category}`}
                  checked={selectedCategories.includes(category)}
                  onCheckedChange={(checked) => handleCategoryToggle(category, checked as boolean)}
                />
                <label
                  htmlFor={`category-${category}`}
                  className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer flex-1"
                >
                  {category}
                </label>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Active Filters Summary */}
      {(selectedCategories.length > 0 || transactionSource || supplier) && (
        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
          <Label className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2 block">
            Active Filters
          </Label>
          <div className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
            {selectedCategories.length > 0 && (
              <div>Categories: {selectedCategories.length} selected</div>
            )}
            {transactionSource && (
              <div>Source: {transactionSource.replace('-', ' ')}</div>
            )}
            {supplier && (
              <div>Supplier: {supplier}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
